import React, { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import 'dayjs/locale/ja'
import 'react-dates/initialize'
import { modalContainerStyle } from '@/components/pages/accommodation-management/list-layout'
import { get as transformHeaderGet } from 'lodash'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { Button } from '@/components/atoms/button'
import { CSVReader } from '@/components/organisms/csv-reader'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import * as salesSubjectLib from '@/libs/sales-subjects'
import { LoaderContextCreator } from '@/contexts/loader'
import * as api from '@/apis/aipass'

type ContainerProps = {
  onClose: () => void
  onComplete: () => void
}

export const SubjectCsvUploadModal: React.FC<ContainerProps> = props => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const { setIsLoading } = useContext(LoaderContextCreator())

  const [uploadSetting, setUploadSetting] = useState<salesSubjectLib.SaleSubjectCsvUploadSettingType>(
    salesSubjectLib.SaleSubjectCsvUploadSettingType.Diff,
  )

  const keyLength = Object.keys(salesSubjectLib.salesSubjectCsvHeaders).length - 1
  const parseOptions = {
    header: true,
    skipEmptyLines: true,
    transformHeader: function (h, i) {
      const isPlanCode = h.includes('プランコード')
      const header = !isPlanCode ? transformHeaderGet(salesSubjectLib.salesSubjectCsvHeaders, h) : `planCode${i - keyLength}`
      return header
    },
  }

  const onFileLoad = async data => {
    try {
      setIsLoading(true)
      let isFormat: boolean = true
      const mappedCsvRows = data.map(value => {
        // get the property
        const planCode: string[] = []
        for (const [key, v] of Object.entries(value)) {
          // Check if it is a csv but not another format (reservation etc.)
          if (key === 'undefined') {
            isFormat = false
          }
          // Put those with planCode in the key name in the array called planCode
          const regex: RegExp = /planCode/
          const matchedKey = key.match(regex)
          if (matchedKey) {
            // Delete plan code with ''
            if (v !== '') {
              planCode.push(`${v}`)
            }
          }
        }

        return {
          displayOrder: value.displayOrder,
          departmentCode: value.departmentCode,
          departmentName: value.departmentName,
          subjectCode: value.subjectCode,
          name: value.subjectName,
          subSubjectCode: value.subSubjectCode,
          subSubjectName: value.subSubjectName,
          hasSeparated: value.hasSeparated,
          category: value.category,
          price: value.price,
          tax: value.tax,
          plans: planCode,
        }
      })

      if (!isFormat) {
        window.alert(t('Incorrect file check it again please'))
        return
      }

      if (!validateCsvRows(mappedCsvRows)) {
        return
      }
      await api.importSalesSubject(mappedCsvRows, uploadSetting)
      props.onComplete()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const validateCsvRows = (rows: any[]): boolean => {
    // Check for blank required fields
    let isNotBlank = true
    let isNumber = true
    let isPregMatch = true
    let isCategoriesMatch = true
    let matchedKeyRequired: string[] | null
    let matchedKeyNumber: string[] | null
    let matchedKeyPreg: string[] | null
    let matchedCategories: string[] | null
    const requiredArray: any[] = []
    const numberArray: any[] = []
    const pregArray: any[] = []
    const categoriesArray: any[] = []

    rows.map(value => {
      for (const [key, v] of Object.entries(value)) {
        // Put the combination with the corresponding key name into the array
        const requiredRegex: RegExp =
          /departmentCode|departmentName|subjectCode|subjectName|subSubjectCode|subSubjectName|hasSeparated|category/
        const numberRegex: RegExp = /price|tax/
        const pregRegex: RegExp = /departmentCode|subjectCode/
        const categoryRegex: RegExp = /category/
        // If the key name is the corresponding one, put it in the new matched array
        matchedKeyRequired = key.match(requiredRegex)
        matchedKeyNumber = key.match(numberRegex)
        matchedKeyPreg = key.match(pregRegex)
        matchedCategories = key.match(categoryRegex)
        if (matchedKeyRequired) {
          if (matchedKeyPreg) {
            pregArray.push(String(v))
          }
          requiredArray.push(`${v}`)
          if (matchedKeyNumber) {
            numberArray.push(Number(v))
          }
          if (matchedCategories) {
            categoriesArray.push(v)
          }
        } else if (matchedKeyNumber) {
          numberArray.push(Number(v))
        }
      }

      const blankCheck = currentValue => {
        return currentValue !== ''
      }
      const numberCheck = currentValue => {
        if (!isNaN(currentValue)) {
          return typeof currentValue == 'number'
        } else {
          return false
        }
      }
      const pregCheck = currentValue => {
        if (/^[0-9a-zA-Z]+$/.test(currentValue)) {
          return true
        } else {
          return false
        }
      }
      const categoriesCheck = currentValue => {
        return currentValue === '客室売上' || currentValue === 'その他売上' || currentValue === '仮受'
      }
      // If there is even one blank value, it becomes false
      isNotBlank = requiredArray.every(blankCheck)
      // If there is even one non-Number, it becomes false.
      isNumber = numberArray.every(numberCheck)
      // If even one piece is not a half-width alphanumeric character, it becomes false.
      isPregMatch = pregArray.every(pregCheck)

      isCategoriesMatch = categoriesArray.every(categoriesCheck)
    })

    if (isNotBlank && isNumber && isPregMatch && isCategoriesMatch) {
      return true
    } else if (!isNotBlank && isNumber && isPregMatch) {
      window.alert(t('A required item has not been entered check it again please'))
      return false
    } else if (isNotBlank && !isNumber && isPregMatch) {
      window.alert(t('Please enter numbers only, without symbols or spaces'))
      return false
    } else if (!isNotBlank && !isNumber) {
      window.alert(
        t('Required items have not been entered check it again please The format is incorrect Please enter in single-byte number'),
      )
      return false
    } else if (!isPregMatch) {
      window.alert(t('The format is incorrect Enter the department code and subject code in single-byte alphanumeric characters'))
      return false
    } else if (!isCategoriesMatch) {
      window.alert(t('Some category format is incorrect'))
      return false
    }
    return true
  }

  return (
    <div css={modalContainerStyle}>
      <div css={modalBodyContainerStyle}>
        <h3 css={modalHeaderStyle}>{t('CSV upload')}</h3>
        <div css={modalMainContainerStyle}>
          <div css={modalMainTopContainerStyle}>CSVアップロードの設定</div>
          <RadioGroupField
            value={uploadSetting}
            items={[
              { value: salesSubjectLib.SaleSubjectCsvUploadSettingType.Diff, label: '差分のみ取り込み' },
              { value: salesSubjectLib.SaleSubjectCsvUploadSettingType.Full, label: '全件削除して再登録' },
            ]}
            groupCss={css({ gap: 24 })}
            onChange={e => {
              setUploadSetting(e.target.value)
            }}
          />
        </div>
        <div css={modalButtonSectionStyle}>
          <Button width={110} height={38} buttonType={3} marginRight={16} onClick={() => props.onClose()}>
            {t('Cancel')}
          </Button>
          <Button width={110} height={40} buttonType={1}>
            <CSVReader
              inputStyle={{ display: 'none', cursor: 'pointer' }}
              label={t('Upload')}
              parserOptions={parseOptions}
              onFileLoaded={onFileLoad}
            />
          </Button>
        </div>
      </div>
    </div>
  )
}

const modalBodyContainerStyle = css({
  position: 'relative',
  top: '42%',
  left: '50%',
  width: 640,
  zIndex: 102,
  opacity: 1,
  background: '#FFF',
  borderRadius: 5,
  transform: 'translate(-50%, -50%)',
})

const modalHeaderStyle = css({
  height: 44,
  margin: '22px 32px',
  paddingTop: 22,
  fontWeight: 'bold',
  fontSize: 18,
  color: '#272727',
})

const modalMainContainerStyle = css({
  padding: '26px 32px',
  backgroundColor: '#F2F2F2',
})

const modalMainTopContainerStyle = css({
  display: 'flex',
  fontSize: 12,
  marginBottom: 12,
  fontWeight: 'bold',
  color: '#676767',
})

const modalButtonSectionStyle = css({
  display: 'flex',
  padding: '11px 32px 11px 32px',
  justifyContent: 'flex-end',
  boxShadow: '0px 0px 6px #0000001A',
})
